import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Container,
  Row,
  Table,
  UncontrolledTooltip
} from 'reactstrap';
import SimpleHeader from 'components/Headers/SimpleHeader.js';
import { Link } from 'react-router-dom';
import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { GET_PRESET_DATA_SETS } from 'graphql/queries';
import { PRESET_DATA_SET_DELETE } from 'graphql/mutations';
import useConfirmation from 'hooks/useConfirmation';
import useNotificationAlert from 'hooks/useNotificationAlert';
import usePagination from 'hooks/usePagination';
import Pagination from 'components/Pagination';
import PresetPageWrapper from './PresetPageWrapper';
import { PRESET_DATA_FULL_PATH } from 'constants/paths';
import { useSortBy, useTable } from 'react-table';
import { useSelector } from 'react-redux';

const sortingKeys = {
  name: 'NAME'
};

const perPage = 10;
const TABLE_STATE_STORAGE_KEY = 'preset_table_state';

const getInitialTableState = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const rawState = window.localStorage.getItem(TABLE_STATE_STORAGE_KEY);

    if (!rawState) {
      return {};
    }

    const parsedState = JSON.parse(rawState);
    const sortBy = Array.isArray(parsedState?.sortBy) ? parsedState.sortBy : [];
    const validSortBy = sortBy
      .filter((item) => item?.id && Object.prototype.hasOwnProperty.call(sortingKeys, item.id))
      .map((item) => ({ id: item.id, desc: Boolean(item.desc) }));

    return { sortBy: validSortBy };
  } catch (err) {
    return {};
  }
};

function Preset(props) {
  const { me } = useSelector((state) => state.auth);
  const indexValueLabel = me?.team?.whiteLabelAccount ? 'Index Value' : 'Gather Index Value';

  const { pageNumber, goToPage } = usePagination({ props });

  const initialTableState = useMemo(() => getInitialTableState(), []);

  const columns = useMemo(
    () => [
      {
        Header: ({ column }) => (
          <span className="heading-w-icon">
            Data Set{' '}
            {column.isSorted ? (
              column.isSortedDesc ? (
                <span className="sorted-icon sorted-icon__desc" />
              ) : (
                <span className="sorted-icon sorted-icon__asc" />
              )
            ) : (
              <span className="sorted-icon" />
            )}
          </span>
        ),
        accessor: 'name'
      },
      {
        Header: () => 'Manage',
        id: 'action',
        disableSortBy: true
      }
    ],
    []
  );

  const {
    loading: isLoadingDatas,
    data,
    refetch,
    networkStatus
  } = useQuery(GET_PRESET_DATA_SETS, {
    variables: { first: perPage, page: pageNumber },
    notifyOnNetworkStatusChange: true
  });

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    state: tableState
  } = useTable(
    {
      columns,
      data: data?.getPresetDataSets?.data || [],
      manualSortBy: true,
      disableSortRemove: true,
      initialState: initialTableState
    },
    useSortBy
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(
        TABLE_STATE_STORAGE_KEY,
        JSON.stringify({ sortBy: tableState.sortBy || [] })
      );
    } catch (err) {
      // Ignore storage errors.
    }
  }, [tableState.sortBy]);

  const refetchWithSort = useCallback(() => {
    refetch({
      orderBy: tableState.sortBy[0]
        ? [
            {
              column: sortingKeys[tableState.sortBy[0].id],
              order: tableState.sortBy[0].desc ? 'DESC' : 'ASC'
            }
          ]
        : undefined
    });
  }, [tableState, refetch]);

  useEffect(() => {
    refetchWithSort();
  }, [refetchWithSort]);

  const { show: showNotificationAlert } = useNotificationAlert({
    place: 'br'
  });

  const [presetDataSetDelete, { loading: isLoadingDestroy }] = useMutation(PRESET_DATA_SET_DELETE);

  const { showConfirmation, hideConfirmation } = useConfirmation(isLoadingDestroy);

  const onDelete = async (id) => {
    try {
      await presetDataSetDelete({ variables: { id: id } });

      showNotificationAlert({
        title: 'Success',
        message: 'You have deleted the preset successfully'
      });
      refetch();
    } catch (err) {
      switch (err.message) {
        default:
          showNotificationAlert({
            type: 'danger',
            title: 'Failed',
            message: err.message
          });
          break;
      }
    } finally {
      hideConfirmation();
    }
  };

  return (
    <PresetPageWrapper>
      <SimpleHeader
        name="Preset Data"
        description={
          <div>
            <p>Preset Data has multiple uses:</p>
            <ol>
              <li>
                In it's simplest form it increases your efficiency by enabling you to create data
                sets that you can refer to in your templates. This reduces the need to retype these
                each time. For example demographic information such as Age Groups. In addition, you
                can use the Preset Data area to set up locations, departments, teams and more.
              </li>
              <li>
                Use the Preset Data to set-up your benchmarking. Simply create your data set such as
                locations, then add your locations. In your template add a question type of "Preset
                Data" and select your data set. In results you will see "{indexValueLabel}" this is
                where you will see the benchmarked results of data from, in this example, locations.
                However, this can be any data set such as departments, teams, etc.
              </li>
              <li>
                Using Preset Data in combination with user privileges, which you can locate from the
                menu by selecting "Company", you can ensure employees only have the rights to see
                data relative to them. Am example would be a manager could see their locations data,
                but not other locations data.
              </li>
            </ol>
          </div>
        }
        rightElements={
          <>
            <Link
              to={`${PRESET_DATA_FULL_PATH}/add`}
              className="btn btn-neutral btn-sm rounded-pill"
              color="default"
              size="sm">
              Add New Data Set
            </Link>
          </>
        }
      />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <Card>
              <CardHeader className="border-0">
                <Row>
                  <Col>
                    <h3 className="mb-0 text-brand">List Preset Data</h3>
                  </Col>
                </Row>
              </CardHeader>

              {(isLoadingDatas || networkStatus === NetworkStatus.refetch) && (
                <CardBody>
                  <p className="text-center">Loading...</p>
                </CardBody>
              )}

              {!isLoadingDatas &&
                networkStatus !== NetworkStatus.refetch &&
                data?.getPresetDataSets?.data.length <= 0 && (
                  <CardBody>
                    <p className="text-center">There is no data!</p>
                  </CardBody>
                )}

              {!isLoadingDatas &&
                networkStatus !== NetworkStatus.refetch &&
                data?.getPresetDataSets?.data.length >= 1 && (
                  <>
                    <Table
                      className="align-items-center table-flush"
                      responsive
                      striped
                      {...getTableProps()}>
                      <thead className="thead-light">
                        {headerGroups.map((headerGroup) => (
                          <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                              <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                {column.render('Header')}
                              </th>
                            ))}
                          </tr>
                        ))}
                      </thead>
                      <tbody {...getTableBodyProps()}>
                        {data.getPresetDataSets?.data.map((preset) => (
                          <tr key={`preset-item-${preset.id}`}>
                            <td>{preset.name}</td>
                            <td className="table-actions">
                              <div className="d-flex">
                                <Link
                                  className="table-action"
                                  to={`${PRESET_DATA_FULL_PATH}/${preset.id}/edit`}
                                  id={`tooltip-edit-${preset.id}`}>
                                  <i className="fas fa-user-edit" />
                                </Link>
                                <UncontrolledTooltip delay={0} target={`tooltip-edit-${preset.id}`}>
                                  Edit Preset Data
                                </UncontrolledTooltip>

                                <button
                                  className="table-action table-action-delete"
                                  id={`tooltip-delete-${preset.id}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    showConfirmation({
                                      title: 'Are you sure want to delete?',
                                      onConfirm: () => onDelete(preset.id),
                                      confirmTitle: 'Delete'
                                    });
                                  }}>
                                  <i className="fas fa-trash" />
                                </button>
                                <UncontrolledTooltip
                                  delay={0}
                                  target={`tooltip-delete-${preset.id}`}>
                                  Delete Preset Data
                                </UncontrolledTooltip>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    <CardFooter className="py-4">
                      <Pagination
                        totalRow={data.getPresetDataSets?.paginatorInfo.total}
                        perPage={perPage}
                        initialPage={pageNumber - 1}
                        onPageChange={(data) => {
                          goToPage(data);
                        }}
                      />
                    </CardFooter>
                  </>
                )}
            </Card>
          </div>
        </Row>
      </Container>
    </PresetPageWrapper>
  );
}

export default Preset;
